import React, { useState } from 'react';
import { ActivityIndicator, Modal, Pressable, ScrollView, StyleSheet, View, useWindowDimensions } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { WebView } from 'react-native-webview';
import { useTheme } from '@/context/ThemeContext';
import { isDarkTheme } from '@/constants/Colors';
import { PLUS_MONTHLY_CREDITS_DISPLAY, PLUS_PREMIUM_MONTHLY_CREDITS_DISPLAY } from '@/constants/config';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import Logo from '@/components/Icons/Logo';

const LEGAL_PAGES = {
  terms: { title: 'Terms of Service', uri: 'https://gab.ai/terms-of-service' },
  privacy: { title: 'Privacy Policy', uri: 'https://gab.ai/privacy-policy' },
  eula: { title: 'Terms of Use', uri: 'https://www.apple.com/legal/internet-services/itunes/dev/stdeula/' },
};

const BENEFITS = [
  { icon: 'briefcase', title: 'Work that gets done', detail: 'Delegate research, writing, and more.' },
  { icon: 'coins', title: `${PLUS_MONTHLY_CREDITS_DISPLAY} credits every month`, detail: 'For premium models, images, and video.' },
  { icon: 'waveform', title: 'Voice conversations', detail: 'Talk through ideas in real time.' },
];

export default function NativePaywallScreen({
  platform,
  selectedTier = 'plus',
  onSelectTier,
  selectedPlan,
  onSelectPlan,
  weeklyPrice,
  yearlyPrice,
  premiumMonthlyPrice,
  premiumYearlyPrice,
  onPurchase,
  onRestore,
  onClose,
  onRetry,
  isPurchasing = false,
  isRestoring = false,
  purchasePending = false,
  storeStatus,
  error,
}) {
  const { theme } = useTheme();
  const insets = useSafeAreaInsets();
  const { height, width, fontScale } = useWindowDimensions();
  const [page, setPage] = useState(null);
  const dark = isDarkTheme(theme);
  const palette = dark
    ? { background: '#111112', ink: '#F5F5F7', secondary: '#A1A1A6', surface: '#1D1D1F', border: '#38383B', selected: '#232325', buttonText: '#111112' }
    : { background: '#FFFFFF', ink: '#1D1D1F', secondary: '#6E6E73', surface: '#F5F5F7', border: '#DEDEE3', selected: '#F5F5F7', buttonText: '#FFFFFF' };
  const styles = createStyles(palette, height < 800, width < 360 || fontScale > 1.3);
  const apple = platform === 'ios';
  const storeName = apple ? 'App Store' : 'Google Play';
  const busy = isPurchasing || isRestoring;
  const purchaseBlocked = busy || purchasePending;
  const premium = apple && selectedTier === 'premium';
  const name = premium ? 'Plus Premium' : 'Plus';
  const monthlyCredits = premium ? PLUS_PREMIUM_MONTHLY_CREDITS_DISPLAY : PLUS_MONTHLY_CREDITS_DISPLAY;
  const selectedPrice = selectedPlan === 'annual' ? (premium ? premiumYearlyPrice : yearlyPrice) : premium ? premiumMonthlyPrice : weeklyPrice;
  const period = selectedPlan === 'annual' ? 'year' : premium ? 'month' : 'week';
  const legalPage = LEGAL_PAGES[page];
  const plans = [
    { id: premium ? 'monthly' : 'weekly', title: premium ? 'Monthly' : 'Weekly', price: premium ? premiumMonthlyPrice : weeklyPrice, period: premium ? 'month' : 'week' },
    { id: 'annual', title: 'Annual', price: premium ? premiumYearlyPrice : yearlyPrice, period: 'year' },
  ];

  const link = (label, action, accessibilityLabel = label) => (
    <Pressable accessibilityRole="button" accessibilityLabel={accessibilityLabel} onPress={action} style={({ pressed }) => [styles.link, pressed && styles.pressed]}>
      <Text size="xs" style={styles.secondary}>{label}</Text>
    </Pressable>
  );

  return (
    <View style={[styles.screen, { paddingTop: insets.top }]}>
      <View style={styles.toolbar}>
        <Pressable
          accessibilityRole="button"
          accessibilityLabel="Restore purchases"
          accessibilityState={{ disabled: busy, busy: isRestoring }}
          onPress={onRestore}
          disabled={busy}
          style={({ pressed }) => [styles.restore, pressed && styles.pressed, busy && styles.disabled]}
        >
          <Text size="s" style={styles.secondary}>{isRestoring ? 'Restoring…' : 'Restore'}</Text>
        </Pressable>
        <Pressable accessibilityRole="button" accessibilityLabel="Close paywall" onPress={onClose} style={({ pressed }) => [styles.close, pressed && styles.pressed]}>
          <Icon name="xmark" size={17} color={palette.secondary} />
        </Pressable>
      </View>

      <ScrollView bounces={false} showsVerticalScrollIndicator={false} contentContainerStyle={[styles.scrollContent, { paddingBottom: Math.max(insets.bottom, 16) }]}>
        <View style={styles.content}>
          <View style={styles.hero}>
            <View accessible={false} accessibilityElementsHidden importantForAccessibility="no-hide-descendants" style={styles.mark}>
              <Logo width={30} height={30} color={palette.ink} />
            </View>
            <Text accessibilityRole="header" weight="bold" size="xxxl" style={[styles.ink, styles.title]}>Do more with {name}.</Text>
            <Text size="m" style={[styles.secondary, styles.subtitle]}>Bring your bigger ideas to life.</Text>
          </View>

          <View style={styles.benefits}>
            {(premium ? [
              { icon: 'coins', title: `${monthlyCredits} credits every month`, detail: '6× the credits included with Plus.' },
              { icon: 'briefcase', title: 'Up to 10 Work sessions', detail: 'Keep more projects moving at once.' },
              { icon: 'headset', title: 'Priority support', detail: 'Plus higher API rate limits.' },
            ] : BENEFITS).map(benefit => (
              <View key={benefit.icon} style={styles.benefit}>
                <View accessible={false} accessibilityElementsHidden importantForAccessibility="no-hide-descendants" style={styles.benefitIcon}>
                  <Icon name={benefit.icon} size={20} color={palette.ink} />
                </View>
                <View style={styles.benefitCopy}>
                  <Text size="m" weight="bold" style={styles.ink}>{benefit.title}</Text>
                  <Text size="s" style={[styles.secondary, styles.benefitDetail]}>{benefit.detail}</Text>
                </View>
              </View>
            ))}
          </View>

          {apple && onSelectTier && <View accessibilityRole="radiogroup" accessibilityLabel="Subscription tier" style={styles.tiers}>
            {[['plus', 'Plus'], ['premium', 'Plus Premium']].map(([id, label]) => <Pressable
              key={id} accessibilityRole="radio" accessibilityLabel={`Choose ${label}`}
              accessibilityState={{ checked: selectedTier === id, disabled: purchaseBlocked }}
              disabled={purchaseBlocked} onPress={() => onSelectTier(id)}
              style={[styles.tier, selectedTier === id && styles.selectedTier]}>
              <Text size="s" weight={selectedTier === id ? 'bold' : 'medium'} style={styles.ink}>{label}</Text>
            </Pressable>)}
          </View>}
          <View accessibilityRole="radiogroup" accessibilityLabel={`Gab AI ${name} plans`} style={styles.plans}>
            {plans.map(plan => {
              const selected = selectedPlan === plan.id;
              const disabled = purchaseBlocked || !plan.price;
              return (
                <Pressable
                  key={plan.id}
                  accessibilityRole="radio"
                  accessibilityLabel={`Gab AI ${name} ${plan.title}, ${plan.price ? `${plan.price} per ${plan.period}` : 'price unavailable'}`}
                  accessibilityState={{ checked: selected, disabled }}
                  onPress={() => onSelectPlan(plan.id)}
                  disabled={disabled}
                  style={({ pressed }) => [styles.plan, selected && styles.selectedPlan, !plan.price && styles.disabled, pressed && styles.pressed]}
                >
                  <View style={styles.planHeading}>
                    <View style={[styles.radio, selected && styles.selectedRadio]}>{selected && <View style={styles.radioDot} />}</View>
                    <Text size="m" weight="bold" style={[styles.ink, styles.planTitle]}>{plan.title}</Text>
                  </View>
                  <View style={styles.price}>
                    <Text size="l" weight="bold" style={styles.ink}>
                      {plan.price || 'Unavailable'}{plan.price && <Text size="s" style={styles.secondary}> / {plan.period}</Text>}
                    </Text>
                  </View>
                </Pressable>
              );
            })}
          </View>

          <View style={styles.checkout}>
            <Pressable
              accessibilityRole="button"
              accessibilityLabel={selectedPrice ? `Subscribe to Gab AI ${name} for ${selectedPrice} per ${period}` : 'Store pricing unavailable'}
              accessibilityState={{ disabled: purchaseBlocked || !selectedPrice, busy: isPurchasing }}
              onPress={onPurchase}
              disabled={purchaseBlocked || !selectedPrice}
              style={({ pressed }) => [styles.subscribe, (purchaseBlocked || !selectedPrice) && styles.disabled, pressed && styles.pressed]}
            >
              {isPurchasing && <ActivityIndicator color={palette.buttonText} />}
              <Text size="l" weight="bold" style={styles.subscribeText}>{isPurchasing ? 'Processing…' : `Subscribe to ${name}`}</Text>
            </Pressable>
            <Text size="xs" style={[styles.secondary, styles.renewal]}>
              {selectedPrice ? `${selectedPrice} / ${period}. Renews automatically.\nCancel anytime in ${apple ? 'Settings' : 'Google Play'}.` : `Pricing will appear when ${storeName} connects.`}
            </Text>
            {!!(error || storeStatus || !selectedPrice) && (
              <View accessibilityLiveRegion="polite" style={styles.storeMessage}>
                <Text size="s" style={[styles.secondary, styles.centered]}>{error || storeStatus || 'This plan is currently unavailable.'}</Text>
                {onRetry && !purchaseBlocked && link('Retry store connection', onRetry)}
              </View>
            )}
          </View>
          <View style={styles.footer}>
            {link('Subscription details', () => setPage('details'))}
            <View style={styles.legalLinks}>
              {link('Terms', () => setPage('terms'), 'Terms of Service')}
              {link('Privacy', () => setPage('privacy'), 'Privacy Policy')}
              {apple && link('Terms of Use', () => setPage('eula'))}
            </View>
          </View>
        </View>
      </ScrollView>

      <Modal visible={!!page} animationType="slide" presentationStyle="pageSheet" onRequestClose={() => setPage(null)}>
        <View style={[styles.screen, { paddingTop: apple ? 12 : insets.top, paddingBottom: insets.bottom }]}>
          <View style={styles.sheetHeader}>
            <Text accessibilityRole="header" size="l" weight="bold" style={[styles.ink, styles.sheetTitle]}>{legalPage?.title || 'Subscription details'}</Text>
            <Pressable accessibilityRole="button" accessibilityLabel="Close subscription details" onPress={() => setPage(null)} style={styles.close}>
              <Icon name="xmark" size={17} color={palette.secondary} />
            </Pressable>
          </View>
          {legalPage ? (
            <WebView style={styles.webview} source={{ uri: legalPage.uri }} />
          ) : (
            <ScrollView contentContainerStyle={styles.details}>
              <Text size="xxl" weight="bold" style={styles.ink}>Gab AI {name}</Text>
              <Text size="m" style={[styles.secondary, styles.detailsText]}>Both {name} plans include Work mode with up to {premium ? 10 : 3} sessions at once, {monthlyCredits} monthly credits, free default-model messages in the app (API excluded), voice conversations, video models, custom agents, and memory.</Text>
              <Text size="l" weight="bold" style={styles.ink}>Billing & cancellation</Text>
              <Text size="m" style={[styles.secondary, styles.detailsText]}>
                {selectedPrice ? `${selectedPrice} is billed ${selectedPlan === 'annual' ? 'annually' : premium ? 'monthly' : 'weekly'}. ` : ''}
                Payment is charged to your {apple ? 'Apple' : 'Google Play'} account at confirmation of purchase. Your subscription automatically renews unless cancelled{apple ? ' at least 24 hours before the current period ends' : ' before the current period ends'}.
                {apple ? ' Your account is charged for renewal within 24 hours before the end of the current period.' : ''}
              </Text>
              <Text size="m" style={[styles.secondary, styles.detailsText]}>Manage or cancel in {apple ? 'Settings → Apple Account → Subscriptions' : 'Google Play → Payments & subscriptions → Subscriptions'}. You can keep using Plus until your paid period ends.</Text>
              <Text size="s" style={[styles.secondary, styles.detailsText]}>Plus is optional. Close this screen to continue using Gab AI without a subscription.</Text>
            </ScrollView>
          )}
        </View>
      </Modal>
    </View>
  );
}

function createStyles(p, compact, stackPrices) {
  return StyleSheet.create({
    screen: { flex: 1, backgroundColor: p.background },
    ink: { color: p.ink },
    secondary: { color: p.secondary },
    toolbar: { width: '100%', maxWidth: 520, alignSelf: 'center', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingHorizontal: 16, paddingTop: 4, paddingBottom: 4 },
    restore: { minHeight: 44, justifyContent: 'center', paddingHorizontal: 12 },
    close: { width: 44, height: 44, borderRadius: 22, backgroundColor: p.surface, alignItems: 'center', justifyContent: 'center' },
    scrollContent: { flexGrow: 1, paddingHorizontal: 28 },
    content: { width: '100%', maxWidth: 420, alignSelf: 'center', flexGrow: 1, justifyContent: 'center' },
    hero: { alignItems: 'center', paddingTop: compact ? 12 : 20, paddingBottom: compact ? 26 : 34 },
    mark: { width: 60, height: 60, borderRadius: 20, backgroundColor: p.surface, alignItems: 'center', justifyContent: 'center', marginBottom: 20 },
    title: { textAlign: 'center', letterSpacing: -0.9, lineHeight: 38 },
    subtitle: { textAlign: 'center', marginTop: 8, lineHeight: 22 },
    benefits: { gap: compact ? 18 : 22, paddingHorizontal: 4 },
    benefit: { flexDirection: 'row', alignItems: 'center', gap: 16 },
    benefitIcon: { width: 26, alignItems: 'center' },
    benefitCopy: { flex: 1 },
    benefitDetail: { marginTop: 4, lineHeight: 19 },
    tiers: { flexDirection: 'row', padding: 4, backgroundColor: p.surface, borderRadius: 14, marginTop: 26 },
    tier: { flex: 1, alignItems: 'center', justifyContent: 'center', minHeight: 44, borderRadius: 10 },
    selectedTier: { backgroundColor: p.background, borderWidth: 1, borderColor: p.border },
    plans: { marginTop: 14, gap: 10 },
    plan: { minHeight: 66, borderWidth: 1.5, borderColor: p.border, borderRadius: 18, padding: 17, flexDirection: stackPrices ? 'column' : 'row', flexWrap: 'wrap', alignItems: stackPrices ? 'stretch' : 'center', gap: 12 },
    selectedPlan: { borderColor: p.ink, backgroundColor: p.selected },
    planHeading: { flexDirection: 'row', alignItems: 'center', gap: 12, flexGrow: stackPrices ? 0 : 1, flexShrink: 1, minWidth: 96, maxWidth: '100%' },
    planTitle: { flexShrink: 1 },
    radio: { width: 20, height: 20, borderRadius: 10, borderWidth: 1.5, borderColor: p.border, alignItems: 'center', justifyContent: 'center' },
    selectedRadio: { borderColor: p.ink, backgroundColor: p.ink },
    radioDot: { width: 6, height: 6, borderRadius: 3, backgroundColor: p.buttonText },
    price: { flexShrink: 1, maxWidth: '100%', marginLeft: stackPrices ? 32 : 0 },
    checkout: { marginTop: 20 },
    subscribe: { minHeight: 56, paddingVertical: 16, paddingHorizontal: 20, borderRadius: 18, backgroundColor: p.ink, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 10 },
    subscribeText: { color: p.buttonText, textAlign: 'center', flexShrink: 1 },
    renewal: { textAlign: 'center', lineHeight: 18, marginTop: 12 },
    footer: { alignItems: 'center', paddingTop: 2 },
    link: { minHeight: 44, paddingHorizontal: 12, alignItems: 'center', justifyContent: 'center' },
    legalLinks: { flexDirection: 'row', flexWrap: 'wrap', justifyContent: 'center' },
    pressed: { opacity: 0.65 },
    disabled: { opacity: 0.45 },
    storeMessage: { alignItems: 'center', paddingTop: 12 },
    centered: { textAlign: 'center' },
    sheetHeader: { flexDirection: 'row', alignItems: 'center', padding: 20, gap: 16 },
    sheetTitle: { flex: 1 },
    details: { padding: 24, gap: 16, maxWidth: 520, alignSelf: 'center' },
    detailsText: { lineHeight: 23 },
    webview: { flex: 1, backgroundColor: p.background },
  });
}
